import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  Divider,
  CircularProgress,
  Tab,
  Tabs,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
} from '@mui/material';
import { ArrowBack, Add, People, Edit, Sync } from '@mui/icons-material';
import PITargetDisplay from '../components/PITargetDisplay';
import CandidateListItem from '../components/CandidateListItem';
import CandidateStats from '../components/CandidateStats';
import JobStatusBox from '../components/JobStatusBox';
import { WorkflowChecklistGrouped, WorkflowTemplateSelector } from '../components/Workflow';
import PIJobSyncDialog from '../components/PIJobSyncDialog';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import { candidatesApi, PIJobSyncResults } from '../services/api';

interface Job {
  id: number;
  title: string;
  department: string;
  location: string;
  employment_type: string;
  status: string;
  description: string;
  requirements?: string;
  responsibilities?: string;
  salary_min?: number;
  salary_max?: number;
  posted_date: string;
  application_deadline?: string;
  positions_available: number;
  positions_filled: number;
  candidates?: any[];
  
  // PI Target fields
  ideal_factor_a_min?: number;
  ideal_factor_a_max?: number;
  ideal_factor_b_min?: number;
  ideal_factor_b_max?: number;
  ideal_factor_c_min?: number;
  ideal_factor_c_max?: number;
  ideal_factor_d_min?: number;
  ideal_factor_d_max?: number;
  
  // PI Relationship fields
  requires_a_greater_than_b?: boolean;
  requires_a_greater_than_c?: boolean;
  requires_a_greater_than_d?: boolean;
  requires_b_greater_than_a?: boolean;
  requires_b_greater_than_c?: boolean;
  requires_b_greater_than_d?: boolean;
  requires_c_greater_than_a?: boolean;
  requires_c_greater_than_b?: boolean;
  requires_c_greater_than_d?: boolean;
  requires_d_greater_than_a?: boolean;
  requires_d_greater_than_b?: boolean;
  requires_d_greater_than_c?: boolean;
  
  job_summary?: string;
  behavioral_description?: string;
  owner_id?: number;
  owner_name?: string;
  created_by?: number;
}

const JobDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [job, setJob] = useState<Job | null>(null);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(searchParams.get('tab') === 'candidates' ? 0 : 0);
  const [sortBy, setSortBy] = useState<'match' | 'name' | 'recent'>('match');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [candidateToDelete, setCandidateToDelete] = useState<string | null>(null);
  const [workflowSelectorOpen, setWorkflowSelectorOpen] = useState(false);
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [workflowKey, setWorkflowKey] = useState(0); // To force refresh workflow
  const { user } = useAuth();
  const { showNotification } = useNotification();
  
  const canEditJob = user?.role && ['admin', 'hr'].includes(user.role);
  const canEditCandidate = user?.role && ['admin', 'hr', 'manager'].includes(user.role);
  const canDeleteCandidate = user?.role && ['admin', 'hr'].includes(user.role);

  useEffect(() => {
    if (id) {
      fetchJob(id);
    }
  }, [id]);

  const fetchJob = async (jobId: string) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/jobs/${jobId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch job');
      }

      const jobData = await response.json();
      setJob(jobData);
    } catch (error) {
      console.error('Failed to load job:', error);
      setJob(null);
    } finally {
      setLoading(false);
    }
  };


  const getEmploymentTypeLabel = (type: string) => {
    switch (type) {
      case 'full_time': return 'Full Time';
      case 'part_time': return 'Part Time';
      case 'contract': return 'Contract';
      case 'temporary': return 'Temporary';
      default: return type;
    }
  };

  const formatSalary = (min?: number, max?: number) => {
    if (!min && !max) return 'Not specified';
    if (min && max) return `$${min.toLocaleString()} - $${max.toLocaleString()}`;
    if (min) return `From $${min.toLocaleString()}`;
    if (max) return `Up to $${max.toLocaleString()}`;
    return 'Not specified';
  };

  const handleDeleteClick = (candidateId: string) => {
    setCandidateToDelete(candidateId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!candidateToDelete) return;

    try {
      await candidatesApi.delete(candidateToDelete);
      showNotification('Candidate archived successfully', 'success');
      // Refresh the job data to update the candidate list
      if (id) {
        fetchJob(id);
      }
    } catch (error) {
      showNotification('Failed to archive candidate', 'error');
    } finally {
      setDeleteDialogOpen(false);
      setCandidateToDelete(null);
    }
  };

  const handleEditCandidate = (candidateId: string) => {
    navigate(`/candidates/${candidateId}`);
  };

  const handleSyncComplete = (results: PIJobSyncResults) => {
    const { jobs, candidates } = results;
    const parts = [];
    if (jobs.linked > 0) parts.push(`${jobs.linked} jobs linked`);
    if (jobs.created > 0) parts.push(`${jobs.created} jobs created`);
    if (candidates.imported > 0) parts.push(`${candidates.imported} candidates imported`);

    if (parts.length > 0) {
      showNotification(`Import complete: ${parts.join(', ')}`, 'success');
      if (id) fetchJob(id); // Refresh job data
    } else {
      showNotification('No changes were made', 'info');
    }
  };

  // Sort candidates based on selected criteria
  const sortCandidates = (candidates: any[]) => {
    if (!candidates) return [];
    
    const sorted = [...candidates];
    
    switch (sortBy) {
      case 'match':
        // Sort by match score (highest first), then by name
        return sorted.sort((a, b) => {
          const scoreA = a.position_match_score || 0;
          const scoreB = b.position_match_score || 0;
          if (scoreA !== scoreB) return scoreB - scoreA;
          // If scores are equal, sort by name
          const nameA = `${a.first_name} ${a.last_name}`.toLowerCase();
          const nameB = `${b.first_name} ${b.last_name}`.toLowerCase();
          return nameA.localeCompare(nameB);
        });
        
      case 'name':
        // Sort alphabetically by full name
        return sorted.sort((a, b) => {
          const nameA = `${a.first_name} ${a.last_name}`.toLowerCase();
          const nameB = `${b.first_name} ${b.last_name}`.toLowerCase();
          return nameA.localeCompare(nameB);
        });
        
      case 'recent':
        // Sort by application date (most recent first)
        return sorted.sort((a, b) => {
          const dateA = new Date(a.application_date || a.created_at).getTime();
          const dateB = new Date(b.application_date || b.created_at).getTime();
          return dateB - dateA;
        });
        
      default:
        return sorted;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!job) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/jobs')}
          sx={{ mb: 2 }}
        >
          Back to Jobs
        </Button>
        <Typography variant="h6" color="error">
          Job not found
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate('/jobs')}
        sx={{ mb: 2 }}
      >
        Back to Jobs
      </Button>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
          <Box>
            <Typography variant="h4" gutterBottom>
              {job.title}
            </Typography>
            <Stack direction="row" spacing={2} mb={2}>
              <JobStatusBox
                jobId={job.id}
                currentStatus={job.status}
                onStatusChange={(newStatus) => {
                  // Update the job in the local state
                  setJob(prevJob => 
                    prevJob ? { ...prevJob, status: newStatus } : null
                  );
                }}
                canEdit={canEditJob}
              />
              <Box
                component="span"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: 32,
                  px: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  fontSize: '0.8125rem',
                  fontWeight: 500,
                  color: 'text.secondary',
                  backgroundColor: 'transparent'
                }}
              >
                {getEmploymentTypeLabel(job.employment_type)}
              </Box>
            </Stack>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<Edit />}
              onClick={() => navigate(`/jobs/${job.id}/edit`)}
            >
              Edit Job
            </Button>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => navigate(`/jobs/${job.id}/add-candidate`)}
            >
              Add Candidate
            </Button>
          </Stack>
        </Box>

        <Stack direction="row" spacing={4} mb={3}>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Department
            </Typography>
            <Typography variant="body1">{job.department}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Location
            </Typography>
            <Typography variant="body1">{job.location}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Positions
            </Typography>
            <Typography variant="body1">
              {job.positions_filled}/{job.positions_available}
            </Typography>
          </Box>
          {job.owner_name && (
            <Box>
              <Typography variant="body2" color="text.secondary">
                Owner
              </Typography>
              <Typography variant="body1">
                {job.owner_name}
              </Typography>
            </Box>
          )}
        </Stack>

        <Divider sx={{ my: 3 }} />

        {/* Job Information */}
        <Stack spacing={2}>
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Job Summary
            </Typography>
            <Typography variant="body1" paragraph>
              {job.job_summary || job.description}
            </Typography>
          </Box>
          
          {(job.requirements || job.responsibilities) && (
            <>
              {job.requirements && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Requirements
                  </Typography>
                  <Typography variant="body1" paragraph sx={{ whiteSpace: 'pre-line' }}>
                    {job.requirements}
                  </Typography>
                </Box>
              )}
              
              {job.responsibilities && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Responsibilities
                  </Typography>
                  <Typography variant="body1" paragraph sx={{ whiteSpace: 'pre-line' }}>
                    {job.responsibilities}
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Stack>
      </Paper>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
          <Tab label={`Candidates (${job.candidates?.length || 0})`} />
          <Tab label="Job Details" />
          {process.env.REACT_APP_ENABLE_WORKFLOWS === 'true' && (
            <Tab label="Workflow" />
          )}
        </Tabs>
      </Box>

      {tabValue === 0 && (
        <Box>
          {job.candidates && job.candidates.length > 0 && (
            <CandidateStats candidates={job.candidates} />
          )}
          
          <Paper sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Box display="flex" alignItems="center" gap={2}>
                <Typography variant="h6">Candidates</Typography>
                {job.candidates && job.candidates.length > 1 && (
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Sort by</InputLabel>
                    <Select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value as 'match' | 'name' | 'recent')}
                      label="Sort by"
                    >
                      <MenuItem value="match">Best Match</MenuItem>
                      <MenuItem value="name">Name (A-Z)</MenuItem>
                      <MenuItem value="recent">Most Recent</MenuItem>
                    </Select>
                  </FormControl>
                )}
              </Box>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  startIcon={<Sync />}
                  onClick={() => setSyncDialogOpen(true)}
                >
                  Import from PI
                </Button>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => navigate(`/jobs/${job.id}/add-candidate`)}
                >
                  Add Candidate
                </Button>
              </Stack>
            </Box>

          {(!job.candidates || job.candidates.length === 0) ? (
            <Box textAlign="center" py={4}>
              <People sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No candidates yet
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={3}>
                Start building your candidate pipeline
              </Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => navigate(`/jobs/${job.id}/add-candidate`)}
              >
                Add First Candidate
              </Button>
            </Box>
          ) : (
            <List sx={{ py: 0 }}>
              {sortCandidates(job.candidates).map((candidate) => {
                // Create ideal ranges object if job has PI targets
                const idealRanges = (job.ideal_factor_a_min !== null && job.ideal_factor_a_min !== undefined) ? {
                  a: [job.ideal_factor_a_min!, job.ideal_factor_a_max!] as [number, number],
                  b: [job.ideal_factor_b_min!, job.ideal_factor_b_max!] as [number, number],
                  c: [job.ideal_factor_c_min!, job.ideal_factor_c_max!] as [number, number],
                  d: [job.ideal_factor_d_min!, job.ideal_factor_d_max!] as [number, number],
                } : undefined;

                return (
                  <CandidateListItem
                    key={candidate.id}
                    candidate={candidate}
                    jobId={job.id.toString()}
                    idealRanges={idealRanges}
                    job={job}
                    onEdit={handleEditCandidate}
                    onDelete={handleDeleteClick}
                    canEdit={canEditCandidate}
                    canDelete={canDeleteCandidate}
                  />
                );
              })}
            </List>
          )}
          </Paper>
        </Box>
      )}

      {tabValue === 1 && (
        <Stack spacing={3}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Job Information
            </Typography>
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Posted Date
                </Typography>
                <Typography variant="body1">
                  {new Date(job.posted_date).toLocaleDateString()}
                </Typography>
              </Box>
              {job.application_deadline && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Application Deadline
                  </Typography>
                  <Typography variant="body1">
                    {new Date(job.application_deadline).toLocaleDateString()}
                  </Typography>
                </Box>
              )}
              {(job.salary_min || job.salary_max) && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Salary Range
                  </Typography>
                  <Typography variant="body1">
                    {formatSalary(job.salary_min, job.salary_max)}
                  </Typography>
                </Box>
              )}
              {job.employment_type && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Employment Type
                  </Typography>
                  <Typography variant="body1">
                    {getEmploymentTypeLabel(job.employment_type)}
                  </Typography>
                </Box>
              )}
            </Stack>
          </Paper>
          
          {/* PI Target Display - only show if PI targets are defined */}
          {(job.ideal_factor_a_min !== null && job.ideal_factor_a_min !== undefined) && (
            <PITargetDisplay values={job} />
          )}
        </Stack>
      )}

      {/* Workflow Tab */}
      {process.env.REACT_APP_ENABLE_WORKFLOWS === 'true' && tabValue === 2 && (
        <Box>
          <WorkflowChecklistGrouped 
            key={workflowKey}
            jobId={job.id} 
            jobOwnerId={job.owner_id}
            currentUser={user}
            canEdit={canEditJob || false}
            onUpdate={() => setWorkflowKey(prev => prev + 1)}
          />
          
          {/* Add workflow button if no workflow exists */}
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Button
              variant="outlined"
              onClick={() => setWorkflowSelectorOpen(true)}
            >
              Change Workflow Template
            </Button>
          </Box>
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Archive Candidate</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to archive this candidate? This action can be undone later.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Archive
          </Button>
        </DialogActions>
      </Dialog>

      {/* Workflow Template Selector Dialog */}
      {process.env.REACT_APP_ENABLE_WORKFLOWS === 'true' && (
        <WorkflowTemplateSelector
          open={workflowSelectorOpen}
          onClose={() => setWorkflowSelectorOpen(false)}
          onSelect={() => {
            setWorkflowSelectorOpen(false);
            setWorkflowKey(prev => prev + 1); // Refresh workflow
            showNotification('Workflow template updated successfully', 'success');
          }}
          jobId={job.id}
        />
      )}

      {/* PI Job Sync Dialog */}
      <PIJobSyncDialog
        open={syncDialogOpen}
        onClose={() => setSyncDialogOpen(false)}
        onComplete={handleSyncComplete}
      />
    </Box>
  );
};

export default JobDetail;